import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { REEL_VOICE_IDS } from 'fym-shared/src/music24/reelVoices';
import type { WaveformShape } from 'fym-shared/src/music24/assetStyles';
import { CatalogItem } from '../../../../../redux/api/fymAPI.types';
import { CreateReelOptions, ReelDurationSec, ReelVoiceId, SerializedReel } from '../../types';
import { clearReelReturnDraft, peekReelReturnDraft, registerReelReturnDraft } from '../../credits/creditPackReturn';

const NO_ITEMS: CatalogItem[] = [];

interface UseReelDraftProps {
  accountId: number;
  /** The release the form opens on (the `catalogItemUuid` query parameter). */
  initialCatalogItemUuid?: string;
  /** The catalog rows. The song of the reel is the master of the picked row (D2). */
  items?: CatalogItem[];
}

/**
 * The AI reel form state (design a1, release-kit n4, q3e). The default voice is the first voice, the
 * default length was 30 s. Design z6 (owner answer 2 of 2026-10-08): the reel has no waveform, no
 * voiceover and one length (15 s), so the form has no pickers and the request sends none of them;
 * the state stays only for a draft that comes back from a credit-pack purchase.
 * The Instructions stay undefined until the artist edits them: the field then shows the default text
 * of the theme, and the request sends none (the server uses the same default).
 * `createOptions` stays undefined until the form has a release whose master is ready
 * (`song.masterStatus`), so Generate stays off until then. The reel takes the song from the catalog row, so the options carry no `audioFileId`
 * (design k2). The lyrics go only when the row has none: else the server uses the row's lyrics.
 */
export function useReelDraft({ accountId, initialCatalogItemUuid, items = NO_ITEMS }: UseReelDraftProps) {
  // The form values back from a credit-pack purchase (owner rule 2026-10-06), else the defaults.
  const [restored] = useState(() => peekReelReturnDraft(initialCatalogItemUuid));
  const [catalogItemUuid, setCatalogItemUuid] = useState<string | undefined>(initialCatalogItemUuid);
  const [lyrics, setLyrics] = useState(restored?.lyrics ?? '');
  const [waveform, setWaveform] = useState<WaveformShape | undefined>(restored?.waveform);
  const [voiceId, setVoiceId] = useState<ReelVoiceId | null>(restored === null ? REEL_VOICE_IDS[0] : restored.voiceId);
  const [durationSec, setDurationSec] = useState<ReelDurationSec>(restored?.durationSec ?? 30);
  // Undefined: the field shows the default text of the theme, and the request sends none.
  const [instructions, setInstructions] = useState<string | undefined>(restored?.instructions);
  const resetInstructions = useCallback(() => setInstructions(undefined), []);
  useEffect(() => {
    if (restored !== null) {
      clearReelReturnDraft();
    }
  }, [restored]);

  // The form values on screen, for the way back after a credit-pack purchase.
  const current = useRef({ catalogItemUuid, lyrics, waveform, voiceId, durationSec, instructions });
  current.current = { catalogItemUuid, lyrics, waveform, voiceId, durationSec, instructions };
  useEffect(
    () =>
      registerReelReturnDraft(() => {
        const values = current.current;
        return values.catalogItemUuid === undefined ? null : { ...values, catalogItemUuid: values.catalogItemUuid };
      }),
    []
  );

  const item = useMemo(() => items.find((candidate) => candidate.uuid === catalogItemUuid), [catalogItemUuid, items]);
  const isMasterReady = item?.song.masterStatus === 'ready';
  const rowHasLyrics = item?.song.hasLyrics === true;

  /**
   * Fills the choices of a reel into the form ("Change the settings" after a reload). The lyrics
   * are not in the reel, so the form keeps its own.
   */
  const fillFromReel = useCallback((reel: SerializedReel) => {
    if (reel.catalogItemUuid !== null) {
      setCatalogItemUuid(reel.catalogItemUuid);
    }
    setWaveform(reel.waveform);
    setVoiceId(reel.voiceId);
    setDurationSec(reel.durationSec === 15 ? 15 : 30);
    setInstructions(reel.instructions ?? undefined);
  }, []);

  const createOptions = useMemo<CreateReelOptions | undefined>(() => {
    if (catalogItemUuid === undefined || !isMasterReady) {
      return undefined;
    }
    const trimmed = lyrics.trim();
    // Design z6: no waveform, no voiceover, one length. The request sends none of them.
    return {
      accountId,
      catalogItemUuid,
      lyrics: rowHasLyrics || trimmed === '' ? undefined : trimmed,
      ...(instructions === undefined ? {} : { instructions }),
    };
  }, [accountId, catalogItemUuid, instructions, isMasterReady, lyrics, rowHasLyrics]);

  return {
    accountId,
    catalogItemUuid,
    setCatalogItemUuid,
    item,
    lyrics,
    setLyrics,
    waveform,
    setWaveform,
    voiceId,
    setVoiceId,
    durationSec,
    setDurationSec,
    instructions,
    setInstructions,
    resetInstructions,
    fillFromReel,
    createOptions,
  };
}

export type ReelDraft = ReturnType<typeof useReelDraft>;
